import { ArrowUpRight } from "lucide-react"

const caseStudies = [
  {
    number: "01",
    title: "Building a National Cyber Resilience Ecosystem",
    challenge: "UK SMEs faced increasing cyber threats but lacked affordable, trusted support. Existing provision was fragmented and often inaccessible to smaller organisations.",
    approach: "As CEO of the National Cyber Resilience Centre Group, I led the creation of a national partnership model bringing together policing, private sector organisations and academia.",
    outcomes: [
      "Established the Regional Cyber Resilience Centre network across England and Wales",
      "Created a trusted, police-supported model for SME cyber resilience",
      "Delivered practical, affordable services at national scale",
    ],
    impact: "A sustainable ecosystem that improved cyber resilience for SMEs while strengthening trust between business and law enforcement.",
  },
  {
    number: "02",
    title: "Scaling a Home Office-Funded National Cyber Programme",
    challenge: "Police CyberAlarm needed to grow rapidly while maintaining trust, governance and operational integrity across multiple stakeholders.",
    approach: "As Police CyberAlarm Lead, I oversaw programme governance, supplier transition, growth strategy and national stakeholder engagement, balancing operational delivery with long-term sustainability.",
    outcomes: [
      "Strengthened programme governance and delivery model",
      "Supported national expansion of membership",
      "Ensured intelligence value for policing alongside tangible benefits for businesses",
    ],
    impact: "Positioned CyberAlarm as a trusted national policing brand supporting both prevention and disruption of cybercrime.",
  },
]

export function CaseStudies() {
  return (
    <section id="impact" className="py-24 lg:py-32 border-t border-border/30">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="max-w-3xl mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-px bg-primary" />
            <span className="text-xs tracking-[0.3em] uppercase text-muted-foreground font-medium">
              Impact
            </span>
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6 text-balance">
            Delivering national-scale outcomes
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Examples of how strategic leadership and partnership building
            translate into measurable impact.
          </p>
        </div>

        {/* Case studies */}
        <div className="space-y-px bg-border/30">
          {caseStudies.map((study) => (
            <div
              key={study.number}
              className="bg-background p-8 lg:p-12 group"
            >
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Number & title */}
                <div className="lg:col-span-4">
                  <span className="block font-serif text-5xl lg:text-6xl text-border group-hover:text-primary/30 transition-colors duration-500 mb-4">
                    {study.number}
                  </span>
                  <h3 className="font-serif text-xl lg:text-2xl text-foreground leading-tight">
                    {study.title}
                  </h3>
                </div>

                {/* Content */}
                <div className="lg:col-span-8 space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <h4 className="text-xs tracking-widest uppercase text-muted-foreground mb-3">
                        Challenge
                      </h4>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {study.challenge}
                      </p>
                    </div>
                    <div>
                      <h4 className="text-xs tracking-widest uppercase text-muted-foreground mb-3">
                        Approach
                      </h4>
                      <p className="text-sm text-foreground/80 leading-relaxed">
                        {study.approach}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-xs tracking-widest uppercase text-muted-foreground mb-3">
                      Outcomes
                    </h4>
                    <ul className="space-y-2">
                      {study.outcomes.map((outcome, index) => (
                        <li key={index} className="flex items-start gap-3 text-sm text-foreground/80">
                          <ArrowUpRight className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                          {outcome}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="pt-4 border-t border-border/30">
                    <p className="text-sm text-primary italic">
                      {study.impact}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
